import { Rng } from '../../core/rng';
import { DungeonBuilder, type DungeonKit } from '../../zones/DungeonBuilder';
import type { ZoneLayout } from '../../zones/types';

const HELLSTONE = 0xc8a8a4;
const HELL_ROCK = 0xa88880;
const CRIMSON = 0xd04040;
const PHANTOM = 0xd8c8ff;

/** Hollow Throne kit: black walls, red-stone columns, braziers, drops into the abyss. */
const KIT: DungeonKit = {
  wall: { model: 'dungeon_wall', length: 8, depth: 1.2, scale: 0.9 },
  post: { model: 'pillar_intact', every: 2, scale: [0.9, 1.05], tint: HELLSTONE },
  light: { model: 'brazier', every: 2 },
  low: [
    { model: 'pillar_ruin', scale: [0.5, 0.8], tint: HELLSTONE, weight: 2 },
    { model: 'bones' },
  ],
  rocks: [
    { model: 'cliff_chunk', r: 2.9, scale: [0.55, 0.95], tint: HELL_ROCK, weight: 3 },
    { model: 'rock_c', r: 0.7, scale: [1.4, 2.6], tint: HELL_ROCK },
  ],
  clutter: [{ model: 'bones', weight: 3 }, { model: 'rock_b', scale: [0.3, 0.5], tint: HELLSTONE }],
  clutterChance: 0.25,
};

function siegeBuilder(gate: 1 | 2 | 3, subtitle: string, width: number, depth: number): DungeonBuilder {
  return new DungeonBuilder(
    {
      id: `siege_${gate}`, name: 'Warlord Siege: the Hollow Throne', subtitle, kind: 'siege', biome: 'abyss', levelRange: [30, 30], music: 'raid',
      width, depth, seed: 2800 + gate, ground: ['tiles', 'cobble', 'rock', 'ash'], groundScale: [4, 4, 7, 6],
      ambient: 'embers', mapTint: 0x2a0e0e, wallHeight: 10, nearWallHeight: -7, dropDepth: 12, accentAmount: 0.4, kit: KIT,
    },
    new Rng(`siege_${gate}`),
  );
}

/**
 * Resupply antechamber south of the arena, joined to it by a bridge over the
 * abyss. Spawns `start` and `resupply`.
 */
function approach(d: DungeonBuilder, z: number, arenaEdge: number): void {
  d.hall(0, z, 7.5, 5, { y: 0.4, path: true });
  d.corridor([{ x: 0, z: z - 4 }, { x: 0, z: arenaEdge - 2 }], 6, { path: true, edge: 'drop' });
  d.spawn('start', 0, z - 1, Math.PI);
  d.spawn('resupply', 3.2, z + 1.4, Math.PI);
  d.prop('crate', 5.2, z + 3, { rot: 0.3, collider: { w: 0.9, d: 0.9 } });
  d.prop('crate', 6.0, z + 2.1, { rot: 1.1, collider: { w: 0.9, d: 0.9 } });
  d.prop('barrel', 4.3, z + 3.8, { collider: { r: 0.45 } });
  d.prop('banner_pole', -5.6, z + 2.8, { collider: { r: 0.35 }, tint: CRIMSON });
}

/**
 * WARLORD SIEGE — Gate 1: the twin hellhounds. A stadium-shaped arena (76 × 36)
 * with the red hound Kharos at `boss_a` (west) and the blue hound Vesh at
 * `boss_b` (east), 52 m apart so they can be kept separated. Frost shards
 * crust Vesh's half. `siege_1_next` opens (flag `siege_1_cleared`) to Gate 2.
 */
export function buildSiege1(): ZoneLayout {
  const d = siegeBuilder(1, 'Gate I — The Twin Hounds', 150, 120);
  d.hall(0, 0, 18, 18, { y: 0, path: true, round: 0 });
  d.room(-20, 0, 18, { path: true });
  d.room(20, 0, 18, { path: true });
  approach(d, 44, 18);

  d.spawn('boss_a', -26, -1, Math.PI / 2);
  d.spawn('boss_b', 26, -1, -Math.PI / 2);
  d.paintCircle(-26, -1, 4, 3, 1.5);
  d.paintRing(26, -1, 3.2, 4.2, 1, 0.5);
  for (const [x, z, s] of [[31, -12, 1.3], [34, 8, 1.1], [18, 13, 0.9], [14, -14, 1.0]] as const) d.prop('ice_crystal', x, z, { scale: s, rot: x + z, collider: { r: 0.8 } });
  for (const [x, z] of [[-31, -12], [-34, 8], [-18, 13], [-14, -14]] as const) d.prop('brazier', x, z, { collider: { r: 0.5 } });
  d.litPortal({ id: 'siege_1_next', to: 'siege_2', toSpawn: 'start', x: 0, z: -15, radius: 2.2, label: 'The Throne Stair', kind: 'instance', requires: 'siege_1_cleared' }, Math.PI);
  return d.build();
}

/**
 * Throne arena of Gate 2 (r 22). Inner floor r ≤ 14; the outer ring
 * 14 < r ≤ 22 is what breaks away at the ×85 / ×35 "ring break" mechanics.
 */
export const SIEGE_THRONE_RING = { x: 0, z: 0, inner: 14, outer: 22 } as const;

/**
 * WARLORD SIEGE — Gate 2: Vhal'Zeroth on the Hollow Throne. Round arena
 * (r 22) with the throne colossus looming over the north rim. Destructible
 * pillars rise at `pillar_0..3` (NE, SE, SW, NW at r 11, inside the inner
 * ring so they survive the ring break); the Tyrant spawns at `boss`. See
 * SIEGE_THRONE_RING for the inner/outer radii. `siege_2_next` opens (flag
 * `siege_2_cleared`) to Gate 3.
 */
export function buildSiege2(): ZoneLayout {
  const { x, z, inner, outer } = SIEGE_THRONE_RING;
  const d = siegeBuilder(2, 'Gate II — The Hollow Throne', 120, 124);
  d.room(x, z, outer, { path: true });
  approach(d, 44, outer);

  d.spawn('boss', 0, -3, 0);
  DungeonBuilder.ring(x, z, 11, 4, 1.5).forEach((p, i) => {
    d.spawn(`pillar_${i}`, p.x, p.z, 0);
    d.paintCircle(p.x, p.z, 1.8, 1, 0.5);
  });
  // The breakable outer ring reads as cracked, ember-lit stone behind a seam.
  d.paintRing(x, z, inner, outer + 1, 3, 0.3);
  d.paintRing(x, z, inner - 0.5, inner + 0.1, 1, 0.2);
  d.prop('statue_hero', 0, -29, { scale: 1.3, tint: 0x806868 });
  for (const h of [10.8, 1.2]) {
    const p = DungeonBuilder.clock(x, z, outer + 1.6, h);
    d.prop('banner_pole', p.x, p.z, { scale: 1.3, tint: CRIMSON });
  }
  d.litPortal({ id: 'siege_2_next', to: 'siege_3', toSpawn: 'start', x: 0, z: -19.2, radius: 2.2, label: 'Into the Phantom Throne', kind: 'instance', requires: 'siege_2_cleared' }, Math.PI);
  return d.build();
}

/**
 * WARLORD SIEGE — Gate 3: the Tyrant's phantom. A floating platform (r 24)
 * over the void; phantom clones appear at the edge (`edge_0..7`, clockwise
 * from 12:45, skipping the bridge at 6 o'clock) and must be countered to
 * strip the Tyrant's armour; he spawns at `boss`. `siege_3_exit` (flag
 * `siege_3_cleared`) returns to Havenreach.
 */
export function buildSiege3(): ZoneLayout {
  const d = siegeBuilder(3, 'Gate III — The Phantom Throne', 120, 130);
  d.room(0, 0, 24, { path: true, edge: 'drop' });
  approach(d, 48, 24);

  d.spawn('boss', 0, 0, 0);
  DungeonBuilder.ring(0, 0, 21.5, 8, 0.75).forEach((p, i) => d.spawn(`edge_${i}`, p.x, p.z, Math.atan2(-p.x, -p.z)));
  d.rim(0, 0, 24, 8, { model: 'pillar_intact', tint: PHANTOM, scale: 1.1 }, { model: 'pillar_ruin', tint: PHANTOM, scale: 0.8 }, { offset: 0.5, startHour: 0.75 });
  d.paintRing(0, 0, 7.6, 8.4, 1, 0.3);
  d.paintRing(0, 0, 15.6, 16.4, 1, 0.3);
  d.paintCircle(0, 0, 3, 3, 1);
  d.litPortal({ id: 'siege_3_exit', to: 'havenreach', toSpawn: 'fromInstance', x: 0, z: -17, radius: 2.2, label: 'Return to Havenreach', kind: 'exit', requires: 'siege_3_cleared' }, Math.PI);
  return d.build();
}
